// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '@loa/theme';
import { Alert, AppIcon, Badge, Button, DataTable, Panel, ProviderLogo, Skeleton } from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';
import { providerApi } from '../api/providerApi';
import { useAdvisor } from '../contexts/AdvisorContext';
import { LOAStatusBadge } from '../components/LOAStatusBadge';
import { ProviderSettingsModal } from '../components/ProviderSettingsModal';
import { QuoteTermsCard } from '../components/QuoteTermsCard';
import { NewBusinessTemplatesCard } from '../components/NewBusinessTemplatesCard';
import { TabBar } from '../components/TabBar';
import { formatAum } from '../utils/currency';
import type { ProviderDetailDTO } from '../types/provider';
import { LOAStatus } from '../types/loa';

type TabType = 'overview' | 'policies' | 'open-loas' | 'settings';
const VALID_TABS: TabType[] = ['overview', 'policies', 'open-loas', 'settings'];

function getProviderInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function StatBlock({ label, value }: Readonly<{ label: string; value: string | number }>) {
  return (
    <div className="px-4">
      <div className="text-body-03 text-black font-normal leading-normal whitespace-nowrap">{label}</div>
      <div className="text-body-01 font-medium text-black leading-tight mt-1 whitespace-nowrap">{value}</div>
    </div>
  );
}

function ContactRow({ icon, children }: Readonly<{ icon: string; children: React.ReactNode }>) {
  return (
    <div className="flex items-center gap-2 text-body-03 text-black">
      <AppIcon name={icon} size={16} color="#525252" />
      {children}
    </div>
  );
}

export function ProviderDetailPage() {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { advisor } = useAdvisor();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab') as TabType | null;
  const activeTab: TabType = rawTab && VALID_TABS.includes(rawTab) ? rawTab : 'overview';
  const setActiveTab = (tab: TabType) => {
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
  };

  const [providerDetail, setProviderDetail] = useState<ProviderDetailDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [expandedProfileId, setExpandedProfileId] = useState<string | null>(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  const loadProviderDetail = useCallback(async () => {
    if (!providerId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await providerApi.getProviderDetail(providerId);
      setProviderDetail(data);
    } catch (err) {
      console.error('Failed to load provider details:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    loadProviderDetail();
  }, [loadProviderDetail]);

  if (isLoading) {
    return (
      <div className="pb-8 font-google">
        <div className="bg-white rounded-card shadow-card mx-lg my-lg p-6" data-testid="provider-detail-loading">
          <Skeleton variant="paragraph" lines={6} />
        </div>
      </div>
    );
  }

  if (error || !providerDetail) {
    return (
      <div className="pb-8 font-google">
        <div className="bg-white rounded-card shadow-card mx-lg my-lg p-6">
          <Alert variant="error" header="Error Loading Provider">
            <p>{error?.message || 'Failed to load provider details'}</p>
            <Button variant="outlined" onClick={loadProviderDetail}>
              <AppIcon name="refresh" />
              Retry
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  const { provider, summary, openLoas, policies } = providerDetail;
  const totalPolicies = policies.reduce((sum, group) => sum + group.policies.length, 0);

  type AgencyCodeRow = Readonly<{
    key: string;
    type: string;
    code?: string;
    emptyLabel: string;
    effective: boolean;
  }>;

  const agencyCodeRows: AgencyCodeRow[] = [
    {
      key: 'personal',
      type: 'Personal code',
      code: summary.advisorAgencyCode,
      emptyLabel: 'Not set',
      effective: summary.agencyCodeSource === 'ADVISOR',
    },
    {
      key: 'brokerage',
      type: 'Brokerage default',
      code: summary.brokerageAgencyCode,
      emptyLabel: 'Not configured',
      effective: summary.agencyCodeSource === 'BROKERAGE',
    },
  ];

  const agencyCodeColumns: DataTableColumn<AgencyCodeRow>[] = [
    { key: 'type', header: 'Code type', render: (row) => row.type },
    {
      key: 'code',
      header: 'Code',
      render: (row) =>
        row.code
          ? <span className="text-body-03 text-black font-medium">{row.code}</span>
          : <span className="text-body-03 text-grey-03 italic">{row.emptyLabel}</span>,
    },
    {
      key: 'status',
      header: 'Active code',
      width: '140px',
      render: (row) => (
        <Badge color={row.effective ? 'green' : 'grey'} variant={row.effective ? 'filled' : 'outlined'}>
          {row.effective ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  return (
    <div className="pb-8 font-google">
      {/* Provider Header Card */}
      <div className="bg-white rounded-card shadow-card mx-lg my-lg px-6 py-6">
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            {provider.logoUrl ? (
              <ProviderLogo logoUrl={provider.logoUrl} name={provider.name} size={56} shape="rounded" />
            ) : (
              <div
                className="w-14 h-14 rounded-full bg-purple-primary text-white flex items-center justify-center text-body-01 font-medium shrink-0"
                aria-hidden="true"
              >
                {getProviderInitials(provider.name)}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-body-01 font-medium text-black leading-normal truncate">{provider.name}</div>
              <div className="text-body-03 text-grey-02 leading-normal">Provider Code: {provider.code}</div>
            </div>
          </div>

          <div className="flex items-start divide-x divide-grey-05">
            <StatBlock label="Open LOAs" value={summary.openLoasCount} />
            <StatBlock label="Policies" value={summary.totalPoliciesCount} />
            <StatBlock label="Active Relationships" value={summary.activeProfilesCount} />
            <StatBlock label="AUM" value={formatAum(summary.totalAum)} />
          </div>
        </div>
      </div>

      {/* Main Card with Tabs */}
      <div className="bg-white rounded-card shadow-card overflow-hidden mx-lg">
        <div className="px-6 pt-2 border-b border-grey-06">
          <TabBar
            tabs={[
              { key: 'overview', label: 'Overview' },
              { key: 'policies', label: 'Policies', count: totalPolicies },
              { key: 'open-loas', label: 'Open LOAs', count: openLoas.length },
              { key: 'settings', label: 'Settings' },
            ]}
            activeTab={activeTab}
            onTabChange={(key) => setActiveTab(key as TabType)}
          />
        </div>

        <div className="p-6">
          {/* Overview */}
          {activeTab === 'overview' && (
            <div className="flex flex-col gap-6 max-w-2xl" data-testid="provider-tab-overview">
              <div>
                <h3 className="m-0 mb-3 text-body-02 font-medium text-black">Contact information</h3>
                <div className="flex flex-col gap-2">
                  {provider.contactName && (
                    <ContactRow icon="user">
                      <span>{provider.contactName}</span>
                    </ContactRow>
                  )}
                  {provider.contactEmail && (
                    <ContactRow icon="mail">
                      <a href={`mailto:${provider.contactEmail}`} className="text-purple-primary no-underline">
                        {provider.contactEmail}
                      </a>
                    </ContactRow>
                  )}
                  {provider.contactPhone && (
                    <ContactRow icon="phone">
                      <a href={`tel:${provider.contactPhone}`} className="text-purple-primary no-underline">
                        {provider.contactPhone}
                      </a>
                    </ContactRow>
                  )}
                  {provider.websiteUrl && (
                    <ContactRow icon="globe">
                      <a
                        href={provider.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-primary no-underline"
                      >
                        {provider.websiteUrl}
                      </a>
                    </ContactRow>
                  )}
                  {!provider.contactName && !provider.contactEmail && !provider.contactPhone && !provider.websiteUrl && (
                    <span className="text-body-03 text-grey-03 italic">No contact information available.</span>
                  )}
                </div>
              </div>

              {(provider.addressLine1 || provider.townCity || provider.country) && (
                <div>
                  <h3 className="m-0 mb-3 text-body-02 font-medium text-black">Address</h3>
                  <div className="text-body-03 text-black leading-relaxed">
                    {[provider.addressLine1, provider.townCity, provider.stateProvinceCounty, provider.postalCode, provider.country]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Policies by Relationship */}
          {activeTab === 'policies' && (
            <div data-testid="provider-tab-policies">
              {policies.length === 0 ? (
                <div className="rounded-xl bg-lilac-05 flex items-center justify-center py-8">
                  <span className="text-black text-body-02">No policies associated with this provider.</span>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {policies.map((group) => {
                    const isExpanded = expandedProfileId === group.profileId;
                    return (
                      <div key={group.profileId} className="border border-grey-06 rounded-xl overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedProfileId(isExpanded ? null : group.profileId)}
                          className={`w-full text-left px-4 py-3 flex items-center gap-2 cursor-pointer border-0 transition-colors ${isExpanded ? 'bg-lilac-05' : 'bg-white hover:bg-page-bg'}`}
                        >
                          <AppIcon name={isExpanded ? 'chevron up' : 'chevron down'} size={14} />
                          <span className="text-body-03 font-medium text-black">{group.profileName}</span>
                          <span className="text-body-03 text-grey-02">
                            ({group.policies.length} {group.policies.length === 1 ? 'policy' : 'policies'})
                          </span>
                        </button>
                        {isExpanded && (
                          <table className="w-full">
                            <thead>
                              <tr className="bg-lilac-05">
                                <th className="px-4 py-3 text-left text-body-03 font-medium text-black whitespace-nowrap">Policy Number</th>
                                <th className="px-4 py-3 text-left text-body-03 font-medium text-black whitespace-nowrap">Policy Type</th>
                                <th className="px-4 py-3 text-left text-body-03 font-medium text-black whitespace-nowrap">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.policies.map((policy) => (
                                <tr key={policy.id} className="border-t border-grey-06">
                                  <td className="px-4 py-3 text-body-03">
                                    <Link to={`/policies/${policy.id}`} className="text-purple-primary no-underline font-medium">
                                      {policy.policyNumber}
                                    </Link>
                                  </td>
                                  <td className="px-4 py-3 text-body-03 text-black">{policy.policyType}</td>
                                  <td className="px-4 py-3 text-body-03 text-black">{policy.status}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Open LOAs */}
          {activeTab === 'open-loas' && (
            <div data-testid="provider-tab-open-loas">
              {openLoas.length === 0 ? (
                <div className="rounded-xl bg-lilac-05 flex items-center justify-center py-8">
                  <span className="text-black text-body-02">
                    All LOAs for this provider have been completed or there are no LOAs yet.
                  </span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-lilac-05">
                        <th className="px-4 py-3 text-left text-body-03 font-medium text-black whitespace-nowrap">Relationship Name</th>
                        <th className="px-4 py-3 text-left text-body-03 font-medium text-black whitespace-nowrap">Policy Numbers</th>
                        <th className="px-4 py-3 text-left text-body-03 font-medium text-black whitespace-nowrap">Sent Date</th>
                        <th className="px-4 py-3 text-left text-body-03 font-medium text-black whitespace-nowrap">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openLoas.map((loa) => (
                        <tr
                          key={loa.id}
                          className="border-t border-grey-06 hover:bg-page-bg transition-colors cursor-pointer"
                          onClick={() => navigate(`/relationships/${loa.profileId}`)}
                        >
                          <td className="px-4 py-3 text-body-03 font-medium text-black">{loa.profileName}</td>
                          <td className="px-4 py-3 text-body-03 text-black">{loa.policyNumbers.join(', ')}</td>
                          <td className="px-4 py-3 text-body-03 text-black whitespace-nowrap">{formatDate(loa.sentForSigningAt)}</td>
                          <td className="px-4 py-3">
                            <LOAStatusBadge status={loa.status as LOAStatus} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Settings */}
          {activeTab === 'settings' && (
            <div className="flex flex-col gap-6 w-full" data-testid="provider-tab-settings">
              <Panel
                title="Agency code"
                headerActions={
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => setSettingsModalOpen(true)}
                    data-testid="edit-agency-code"
                  >
                    Edit
                  </Button>
                }
              >
                <div className="flex flex-col gap-4">
                  <DataTable<AgencyCodeRow>
                    columns={agencyCodeColumns}
                    data={agencyCodeRows}
                    rowKey={(row) => row.key}
                  />
                  <p className="m-0 text-body-03 text-grey-02">
                    Your personal code takes precedence over the brokerage default when set.
                    The brokerage default is managed by your brokerage admin.
                  </p>
                </div>
              </Panel>

              <QuoteTermsCard providerId={provider.id} />
              <NewBusinessTemplatesCard providerId={provider.id} />
            </div>
          )}
        </div>
      </div>

      {/* Agency-code settings modal */}
      {settingsModalOpen && advisor?.id && (
        <ProviderSettingsModal
          open
          provider={{
            id: provider.id,
            name: provider.name,
            advisorAgencyCode: summary.advisorAgencyCode,
            brokerageAgencyCode: summary.brokerageAgencyCode,
          }}
          advisorId={String(advisor.id)}
          onClose={() => setSettingsModalOpen(false)}
          onSaved={() => {
            setSettingsModalOpen(false);
            void loadProviderDetail();
          }}
        />
      )}
    </div>
  );
}
